/**
 * Holographic Product List - Flight Check Mode
 *
 * Premium product list with:
 * - Large product thumbnails (80px) with hover zoom
 * - Interactive checkboxes for packing verification
 * - Progress bar showing items verified
 * - Visual separation between verified and pending
 * - Qty confirmation modal for multi-qty items
 * - Pack confirmation modal with parcel size selector
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  Checkbox,
  LinearProgress,
  Chip,
  Collapse,
  Tooltip,
  keyframes,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  alpha,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Inventory2 as PackageIcon,
  Cancel as NotFoundIcon,
  HourglassEmpty as PendingPickIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { useMissionTheme } from '../theme';
import GlassCard from './GlassCard';

const scanLine = keyframes`
  0% {
    top: 0;
    opacity: 1;
  }
  100% {
    top: 100%;
    opacity: 0;
  }
`;

const notFoundFlash = keyframes`
  0%, 100% {
    background-color: rgba(239, 68, 68, 0.05);
  }
  50% {
    background-color: rgba(239, 68, 68, 0.12);
  }
`;

export interface PickingLineStatus {
  lineId: string;
  pickingStatus: 'found' | 'not_found';
  pickedQuantity: number;
}

interface ProductLine {
  id: string;
  quantity: number;
  price: number;
  product?: {
    id?: string;
    name?: string;
    sku?: string;
    priceTier1?: number;
    image?: string; // Direct image URL from some APIs
    attachments?: Array<{ thumbnailUrl?: string; url?: string }>;
    categoryRelation?: { name?: string };
  };
}

interface HolographicProductListProps {
  lines: ProductLine[];
  status: string;
  readOnly?: boolean;
  /** Live picking statuses from Socket.IO (keyed by lineId) */
  pickingStatuses?: Map<string, PickingLineStatus>;
  /** User role — controls whether packing flow is active */
  role?: string | null;
  /** Called when staff confirms pack with chosen parcel size */
  onPackConfirmed?: (parcelSize: string) => void;
}

const PARCEL_SIZES = [
  { value: 'small',  label: 'Small',  vehicle: 'Bicycle / scooter', desc: 'Single small items' },
  { value: 'medium', label: 'Medium', vehicle: 'Sedan',             desc: 'Standard grocery order' },
  { value: 'large',  label: 'Large',  vehicle: 'SUV / bakkie',      desc: 'Large or bulky order' },
  { value: 'xlarge', label: 'XLarge', vehicle: 'Truck / van',       desc: 'Catering / bulk' },
] as const;

export default function HolographicProductList({
  lines,
  status,
  readOnly = false,
  pickingStatuses,
  role,
  onPackConfirmed,
}: HolographicProductListProps) {
  const { mc } = useMissionTheme();

  // Keyframe needs mc.successGlow — memoize so it only recreates on mode change
  const checkPulse = useMemo(() => keyframes`
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 ${mc.successGlow};
    }
    50% {
      transform: scale(1.05);
      box-shadow: 0 0 20px ${mc.successGlow};
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 ${mc.successGlow};
    }
  `, [mc.successGlow]);

  // Track verified items (in-memory only, not persisted)
  const [verifiedItems, setVerifiedItems] = useState<Set<string>>(new Set());

  // Qty modal state
  const [qtyModalLine, setQtyModalLine] = useState<{ lineId: string; maxQty: number } | null>(null);
  const [qtyInput, setQtyInput] = useState(1);

  // Pack confirmation modal state
  const [packModalOpen, setPackModalOpen] = useState(false);
  const [packModalDismissed, setPackModalDismissed] = useState(false);
  const [parcelSize, setParcelSize] = useState<'small' | 'medium' | 'large' | 'xlarge'>('medium');
  const [packConfirmed, setPackConfirmed] = useState(false);

  const isOperational = role === 'staff' || role === 'admin' || role === 'owner';
  const isPackingPhase = ['placed', 'packed'].includes(status?.toLowerCase() || '');
  const isPackingActive = isOperational && !readOnly && isPackingPhase;

  // Calculate progress
  const progress = useMemo(() => {
    const total = lines.reduce((sum, line) => sum + Number(line.quantity), 0);
    const verified = lines
      .filter((line) => verifiedItems.has(line.id || line.product?.id || ''))
      .reduce((sum, line) => sum + Number(line.quantity), 0);
    return { total, verified, percent: total > 0 ? (verified / total) * 100 : 0 };
  }, [lines, verifiedItems]);

  const isAllVerified = progress.percent === 100;

  // Fire pack modal when all items are verified
  useEffect(() => {
    if (isPackingActive && isAllVerified && !packModalOpen && !packConfirmed && !packModalDismissed && lines.length > 0) {
      setPackModalOpen(true);
    }
  }, [isAllVerified, isPackingActive, packModalOpen, packConfirmed, packModalDismissed, lines.length]);

  const toggleVerified = (lineId: string, maxQty: number) => {
    if (readOnly) return;
    if (verifiedItems.has(lineId)) {
      // Un-check (correctable before pack confirmation)
      setVerifiedItems((prev) => { const next = new Set(prev); next.delete(lineId); return next; });
      return;
    }
    if (maxQty > 1 && isPackingActive) {
      // Open qty confirmation modal — start at 0 so staff must tap to confirm each unit
      setQtyInput(0);
      setQtyModalLine({ lineId, maxQty });
    } else {
      setVerifiedItems((prev) => new Set(prev).add(lineId));
    }
  };

  const confirmQty = () => {
    if (!qtyModalLine) return;
    setVerifiedItems((prev) => new Set(prev).add(qtyModalLine.lineId));
    setQtyModalLine(null);
  };

  return (
    <GlassCard
      title="Cargo Manifest"
      icon={<PackageIcon />}
      iconColor={isAllVerified ? mc.success : mc.secondary}
      variant={isAllVerified ? 'success' : 'default'}
    >
      {/* Progress Bar */}
      {isPackingPhase && !readOnly && (
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ color: mc.textMuted }}>
              VERIFICATION PROGRESS
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: isAllVerified ? mc.success : mc.textSecondary,
                fontFamily: 'monospace',
                fontWeight: 600,
              }}
            >
              {progress.verified}/{progress.total} ITEMS
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progress.percent}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: mc.surface,
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                bgcolor: isAllVerified ? mc.success : mc.secondary,
                boxShadow: `0 0 10px ${isAllVerified ? mc.successGlow : mc.secondaryGlow}`,
              },
            }}
          />
          {isAllVerified && (
            <Typography
              variant="body2"
              sx={{
                mt: 1.5,
                textAlign: 'center',
                color: mc.success,
                fontWeight: 700,
                letterSpacing: 2,
              }}
            >
              READY FOR DISPATCH
            </Typography>
          )}
        </Box>
      )}

      {/* Product List */}
      <Stack spacing={1.5}>
        {lines.map((line, index) => {
          const lineId = line.id || line.product?.id || `line-${index}`;
          const isVerified = verifiedItems.has(lineId);
          const pickStatus = pickingStatuses?.get(line.id);
          const isPickedFound = pickStatus?.pickingStatus === 'found';
          const isPickedNotFound = pickStatus?.pickingStatus === 'not_found';
          // Try multiple image sources: thumbnailUrl, url, or direct image field
          const imageUrl = line.product?.attachments?.[0]?.thumbnailUrl
            || line.product?.attachments?.[0]?.url
            || line.product?.image;

          return (
            <Box
              key={lineId}
              onClick={() => isPackingPhase && !readOnly && toggleVerified(lineId, Number(line.quantity))}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1.5,
                borderRadius: 2,
                bgcolor: isPickedNotFound
                  ? 'rgba(239, 68, 68, 0.06)'
                  : isPickedFound
                    ? `${mc.success}08`
                    : isVerified
                      ? `${mc.success}10`
                      : mc.surface,
                border: `1px solid ${
                  isPickedNotFound
                    ? '#EF4444'
                    : isPickedFound
                      ? mc.success
                      : isVerified
                        ? mc.success
                        : mc.border
                }`,
                cursor: isPackingPhase && !readOnly ? 'pointer' : 'default',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                animation: isPickedNotFound
                  ? `${notFoundFlash} 2s infinite`
                  : isVerified
                    ? `${checkPulse} 0.5s ease-out`
                    : 'none',
                '&:hover': isPackingPhase && !readOnly ? {
                  bgcolor: isVerified ? `${mc.success}15` : mc.hover,
                  borderColor: isVerified ? mc.success : mc.secondary,
                } : {},
              }}
            >
              {/* Scan Line Effect on Verify */}
              {isVerified && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    height: 2,
                    bgcolor: mc.success,
                    animation: `${scanLine} 0.3s ease-out`,
                    boxShadow: `0 0 10px ${mc.success}`,
                  }}
                />
              )}

              {/* Checkbox */}
              {isPackingPhase && !readOnly && (
                <Checkbox
                  checked={isVerified}
                  icon={<UncheckedIcon sx={{ color: mc.textMuted }} />}
                  checkedIcon={<CheckIcon sx={{ color: mc.success }} />}
                  sx={{
                    p: 0,
                    '& .MuiSvgIcon-root': {
                      fontSize: 28,
                    },
                  }}
                />
              )}

              {/* Product Image */}
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: `1px solid ${mc.border}`,
                  flexShrink: 0,
                  bgcolor: mc.elevated,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  '&:hover img': {
                    transform: 'scale(1.1)',
                  },
                }}
              >
                {imageUrl ? (
                  <Box
                    component="img"
                    src={imageUrl}
                    alt={line.product?.name || 'Product'}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease',
                      filter: isVerified ? 'none' : 'grayscale(30%)',
                    }}
                  />
                ) : (
                  <PackageIcon sx={{ color: mc.textMuted, fontSize: 32 }} />
                )}
              </Box>

              {/* Product Info */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: isVerified ? mc.success : mc.textPrimary,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textDecoration: isVerified ? 'none' : 'none',
                  }}
                >
                  {line.product?.name || 'Unknown Product'}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  {line.product?.sku && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: mc.textMuted,
                        fontFamily: 'monospace',
                        fontSize: '0.7rem',
                      }}
                    >
                      {line.product.sku}
                    </Typography>
                  )}
                  {line.product?.categoryRelation?.name && (
                    <Chip
                      label={line.product.categoryRelation.name}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        bgcolor: mc.elevated,
                        color: mc.textSecondary,
                        border: `1px solid ${mc.border}`,
                      }}
                    />
                  )}
                </Stack>
              </Box>

              {/* Picking Status Icon */}
              {pickStatus && (
                <Tooltip title={
                  isPickedFound
                    ? `Found (${pickStatus.pickedQuantity}/${line.quantity})`
                    : 'Not Found'
                }>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {isPickedFound ? (
                      <CheckIcon sx={{ color: mc.success, fontSize: 24 }} />
                    ) : (
                      <NotFoundIcon sx={{ color: '#EF4444', fontSize: 24 }} />
                    )}
                  </Box>
                </Tooltip>
              )}
              {!pickStatus && pickingStatuses && status?.toLowerCase() === 'picking' && (
                <Tooltip title="Pending pick">
                  <PendingPickIcon sx={{ color: mc.textMuted, fontSize: 20 }} />
                </Tooltip>
              )}

              {/* Quantity */}
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor: mc.elevated,
                  borderRadius: 2,
                  border: `1px solid ${
                    isPickedNotFound ? '#EF4444' : isPickedFound ? mc.success : mc.border
                  }`,
                  textAlign: 'center',
                  minWidth: 60,
                }}
              >
                {pickStatus ? (
                  <>
                    <Typography
                      variant="h6"
                      sx={{
                        color: isPickedNotFound ? '#EF4444' : mc.success,
                        fontWeight: 700,
                        fontFamily: 'monospace',
                        lineHeight: 1,
                      }}
                    >
                      {pickStatus.pickedQuantity}/{line.quantity}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: isPickedNotFound ? '#EF4444' : mc.success,
                        fontSize: '0.6rem',
                        fontWeight: 600,
                      }}
                    >
                      {isPickedFound ? 'FOUND' : 'MISSING'}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography
                      variant="h6"
                      sx={{
                        color: isVerified ? mc.success : mc.textPrimary,
                        fontWeight: 700,
                        fontFamily: 'monospace',
                        lineHeight: 1,
                      }}
                    >
                      {line.quantity}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: mc.textMuted,
                        fontSize: '0.6rem',
                      }}
                    >
                      QTY
                    </Typography>
                  </>
                )}
              </Box>

              {/* Price */}
              <Box sx={{ textAlign: 'right', minWidth: 80 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: mc.textPrimary,
                    fontWeight: 600,
                    fontFamily: 'monospace',
                  }}
                >
                  R{(Number(line.price) * Number(line.quantity)).toFixed(2)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: mc.textMuted,
                    fontSize: '0.65rem',
                  }}
                >
                  @ R{Number(line.price).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>

      {/* ================================================================ */}
      {/* QTY CONFIRMATION MODAL */}
      {/* ================================================================ */}
      <Dialog
        open={!!qtyModalLine}
        onClose={() => setQtyModalLine(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: mc.elevated, border: `1px solid ${mc.border}`, borderRadius: 3 } }}
      >
        <DialogTitle sx={{ color: mc.textPrimary, fontWeight: 700, pb: 1 }}>
          How many did you find?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: mc.textSecondary, mb: 2 }}>
            Ordered: <strong>{qtyModalLine?.maxQty}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
            <IconButton
              onClick={() => setQtyInput(q => Math.max(0, q - 1))}
              sx={{ bgcolor: alpha(mc.error, 0.1), color: mc.error, '&:hover': { bgcolor: alpha(mc.error, 0.2) } }}
            >
              <RemoveIcon />
            </IconButton>
            <Typography variant="h4" sx={{ color: mc.textPrimary, fontWeight: 700, minWidth: 40, textAlign: 'center' }}>
              {qtyInput}
            </Typography>
            <IconButton
              onClick={() => setQtyInput(q => Math.min(qtyModalLine?.maxQty ?? 1, q + 1))}
              sx={{ bgcolor: alpha(mc.success, 0.1), color: mc.success, '&:hover': { bgcolor: alpha(mc.success, 0.2) } }}
            >
              <AddIcon />
            </IconButton>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setQtyModalLine(null)} sx={{ color: mc.textMuted, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmQty}
            sx={{
              bgcolor: mc.success,
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: mc.success, filter: 'brightness(1.1)' },
            }}
          >
            Mark Found
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================================================ */}
      {/* PACK CONFIRMATION MODAL */}
      {/* ================================================================ */}
      <Dialog
        open={packModalOpen}
        onClose={() => { setPackModalOpen(false); setPackModalDismissed(true); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: mc.elevated,
            border: `1px solid ${mc.success}`,
            borderRadius: 3,
            boxShadow: `0 0 30px ${mc.successGlow}`,
          },
        }}
      >
        <DialogTitle sx={{ color: mc.success, fontWeight: 800, letterSpacing: 1 }}>
          ✓ READY TO PACK
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: mc.textSecondary, mb: 3 }}>
            All items verified. Select parcel size for the courier.
          </Typography>

          {/* Parcel size selector */}
          <Stack spacing={1} sx={{ mb: 1 }}>
            {PARCEL_SIZES.map((size) => (
              <Box
                key={size.value}
                onClick={() => setParcelSize(size.value)}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  cursor: 'pointer',
                  border: `1px solid ${parcelSize === size.value ? mc.secondary : mc.border}`,
                  bgcolor: parcelSize === size.value ? alpha(mc.secondary, 0.08) : 'transparent',
                  boxShadow: parcelSize === size.value ? `0 0 12px ${mc.secondaryGlow}` : 'none',
                  transition: 'all 0.2s',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Stack>
                  <Typography sx={{ color: mc.textPrimary, fontWeight: 600, fontSize: '0.9rem' }}>
                    {size.label}
                    {size.value === 'medium' && (
                      <Box component="span" sx={{ ml: 1, fontSize: '0.7rem', color: mc.textMuted }}>(default)</Box>
                    )}
                  </Typography>
                  <Typography sx={{ color: mc.textMuted, fontSize: '0.75rem' }}>{size.desc}</Typography>
                </Stack>
                <Typography sx={{ color: mc.textSecondary, fontSize: '0.78rem' }}>{size.vehicle}</Typography>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => { setPackModalOpen(false); setPackModalDismissed(true); }}
            sx={{ color: mc.textMuted, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setPackModalOpen(false);
              setPackConfirmed(true);
              onPackConfirmed?.(parcelSize);
            }}
            sx={{
              bgcolor: mc.success,
              color: mc.background,
              fontWeight: 800,
              textTransform: 'none',
              px: 3,
              boxShadow: `0 0 20px ${mc.successGlow}`,
              '&:hover': { bgcolor: mc.success, boxShadow: `0 0 30px ${mc.successGlow}`, filter: 'brightness(1.1)' },
            }}
          >
            MARK AS PACKED
          </Button>
        </DialogActions>
      </Dialog>
    </GlassCard>
  );
}
